import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { uniqueDivisionKeys, divisionsForTrade } from '../lib/divisions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
let metaCache = null;
export const loadAccessMeta = async (force = false) => {
  if (!metaCache || force) {
    const [r, d, v] = await Promise.all([
      axios.get(`${API}/admin/roles?include_inactive=false`, opts),
      axios.get(`${API}/inv/departments`, opts),
      axios.get(`${API}/divisions`, opts),
    ]);
    metaCache = { roles: r.data.roles, departments: d.data, divisions: v.data };
  }
  return metaCache;
};

const sel = 'w-full h-10 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2 disabled:opacity-60';

const MultiChips = ({ items, value, onChange, testid, disabled }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {items.map((it) => {
      const on = value.includes(it.id);
      return (
        <button type="button" key={it.id} disabled={disabled} data-testid={`${testid}-${it.id}`} onClick={() => onChange(on ? value.filter((x) => x !== it.id) : [...value, it.id])}
          className={`text-[11px] px-2 py-0.5 rounded-full border ${on ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]' : 'border-[#3f3f3f] text-[#A3A3A3]'}`}>{it.name}</button>
      );
    })}
  </div>
);

// iter315 — Role (record) + Primary Trade + Primary Division + additive extra scope.
export const EmployeeAccessPanel = ({ employeeId, canEdit, onSaved }) => {
  const [meta, setMeta] = useState(null);
  const [acc, setAcc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedDivision, setSavedDivision] = useState('');

  useEffect(() => {
    let live = true;
    Promise.all([loadAccessMeta(), axios.get(`${API}/admin/employees-access`, opts)]).then(([m, a]) => {
      if (!live) return;
      setMeta(m);
      const row = a.data.employees.find((e) => e.id === employeeId) || null;
      setAcc(row);
      setSavedDivision(row?.division || '');
    }).catch(() => toast.error('Failed to load access settings'));
    return () => { live = false; };
  }, [employeeId]);

  if (!meta || !acc) return <p className="text-xs text-[#525252]">Loading access…</p>;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/employees/${employeeId}/access`, {
        role_id: acc.role_id, primary_department_id: acc.primary_department_id, division: acc.division,
        extra_department_ids: acc.extra_department_ids, extra_divisions: acc.extra_divisions,
      }, opts);
      setAcc(data); setSavedDivision(data.division || ''); toast.success('Access saved'); onSaved?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };
  const scopeText = (v, list, key) => (v === 'all' ? 'All' : v.map((x) => list.find((i) => i[key] === x)?.name || x).join(', '));
  // iter317 — Primary Division choices come from the Primary Trade's own Divisions.
  const primaryDivs = divisionsForTrade(meta.divisions, acc.primary_department_id);
  const primaryDivOk = !acc.division || primaryDivs.some((d) => d.key === acc.division);
  const onTradeChange = (tid) => {
    const opts = divisionsForTrade(meta.divisions, tid);
    // Prefer the saved Primary Division when it is valid for the newly picked Trade.
    const pick = [savedDivision, acc.division].find((k) => k && opts.some((d) => d.key === k)) || opts[0]?.key || '';
    setAcc({ ...acc, primary_department_id: tid, division: pick });
  };
  return (
    <div data-testid="employee-access-panel" className="sm:col-span-2 rounded-md border border-[#262626] bg-[#0A0A0A]/60 p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-white">Role</Label>
          <select data-testid="profile-role-record" disabled={!canEdit} value={acc.role_id} onChange={(e) => setAcc({ ...acc, role_id: e.target.value })} className={sel}>
            {meta.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-white">Primary Trade</Label>
          <select data-testid="profile-primary-trade" disabled={!canEdit} value={acc.primary_department_id} onChange={(e) => onTradeChange(e.target.value)} className={`${sel} ${acc.primary_department_id ? '' : 'border-orange-500/60'}`}>
            <option value="">— Trade not assigned —</option>
            {meta.departments.map((d) => <option key={d.id} value={d.id}>{d.name}{d.type === 'internal' ? ' (internal)' : ''}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-white">Primary Division</Label>
          <select data-testid="profile-dept" disabled={!canEdit} value={acc.division} onChange={(e) => setAcc({ ...acc, division: e.target.value })} className={`${sel} ${primaryDivOk ? '' : 'border-orange-500/60'}`}>
            {!primaryDivOk && <option value={acc.division}>{acc.division || '—'} (not set up for this Trade)</option>}
            {primaryDivs.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
          </select>
          {!primaryDivOk && <p data-testid="profile-division-invalid" className="text-[10px] text-orange-300 mt-1">Pick a Division that belongs to the Primary Trade.</p>}
        </div>
      </div>
      {acc.trade_not_assigned && (
        <p data-testid="profile-trade-not-assigned" className="text-[11px] text-orange-300">Trade not assigned — until a Primary Trade is set, this employee temporarily keeps access to all trades (transition fallback).</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[#A3A3A3] text-xs">Extra allowed Trades (exceptions)</Label>
          <MultiChips testid="profile-extra-trades" disabled={!canEdit} items={meta.departments} value={acc.extra_department_ids} onChange={(v) => setAcc({ ...acc, extra_department_ids: v })} />
        </div>
        <div>
          <Label className="text-[#A3A3A3] text-xs">Extra allowed Divisions (exceptions)</Label>
          <MultiChips testid="profile-extra-divisions" disabled={!canEdit} items={uniqueDivisionKeys(meta.divisions).map((d) => ({ id: d.key, name: d.name }))} value={acc.extra_divisions} onChange={(v) => setAcc({ ...acc, extra_divisions: v })} />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-[#7f7f7f]" data-testid="profile-effective-scope">
          Effective scope — Trades: <span className="text-white">{scopeText(acc.effective_departments, meta.departments, 'id')}</span>{acc.trade_fallback_active ? ' (fallback)' : ''} · Divisions: <span className="text-white">{scopeText(acc.effective_divisions, meta.divisions, 'key')}</span>
        </p>
        {canEdit && <Button data-testid="profile-access-save" size="sm" disabled={saving} onClick={save} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-8">{saving ? 'Saving…' : 'Save Access'}</Button>}
      </div>
    </div>
  );
};

export default EmployeeAccessPanel;
